import type { Dispatch } from 'react';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import unset from 'lodash.unset';
import cloneDeep from 'lodash.clonedeep';

import type { FormValues } from '../types/FormValues';
import type { FieldQueryData } from '../context/types/store';
import type { APIField, DynamicField, DynamicFields } from '../fields/types/main';
import type { APIStaticField } from '../fields/StaticField/types';
import { flattenArray } from '../context/utils/flattenArray';
import { getFieldQueryByField } from '../context/utils/getFieldQueryByField';
import { getDependenciesValues } from '../context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../context/utils/getIsDependenciesSatisfied';
import type { TouchedValues } from '../context/ContestManageContext';
import { ContestManageContext } from '../context/ContestManageContext';
import type { StoreAction } from '../context/store/types/actions';
import { parseFieldResponse } from '../context/parsers/parseFieldResponse';
import { createSetFieldAction, createUpdateFieldCacheAction } from '../context/store/store.actions';
import { getManageEndpointData } from '../../../server/service';

import { useEnsureFieldData } from './useEnsureFieldData';

type EnsureDataFunctionProps<F> = {
  field: F;
  fieldKey: string;
  path?: string[];
  sectionKey: string;
  setValues: (fn: (prev: FormValues) => FormValues) => void;
  setTouched: (fn: (prev: TouchedValues) => TouchedValues) => void;
  dispatchStoreAction: Dispatch<StoreAction>;
  payload: FormValues;
  newFieldQueryKey: string;
  abortController: AbortController;
  currentFieldQuery: FieldQueryData | null;
};

const ensureDataForDynamicField = ({
  field,
  dispatchStoreAction,
  payload,
  newFieldQueryKey,
  abortController,
}: EnsureDataFunctionProps<DynamicField>) =>
  getManageEndpointData<APIStaticField>(
    field.endpointName,
    payload,
    field.endpointMethod,
    abortController,
    field.queryParams
  ).then((data) => {
    const normalizedFields = parseFieldResponse(data);
    if (!normalizedFields?.length) {
      return;
    }

    flattenArray(normalizedFields).forEach((normalizedField) => {
      if ('data' in normalizedField) {
        dispatchStoreAction(
          createUpdateFieldCacheAction(newFieldQueryKey, {
            data: normalizedField.data,
            isLoading: false,
            error: undefined,
          })
        );
      }
    });
  });

const ensureDataForDynamicFields = ({
  field,
  fieldKey,
  path = [],
  sectionKey,
  setValues,
  setTouched,
  dispatchStoreAction,
  payload,
  newFieldQueryKey,
  abortController,
  currentFieldQuery,
}: EnsureDataFunctionProps<DynamicFields>) =>
  getManageEndpointData<APIField[]>(
    field.endpointName,
    payload,
    field.endpointMethod,
    abortController,
    field.queryParams
  ).then((data) => {
    // TODO: check if prev values are compatible with new ones before deleting them
    // Condition is here just to make TS happy
    if (currentFieldQuery?.type === 'DynamicFields') {
      setValues((prev) => {
        const newPrev = cloneDeep(prev);
        unset(newPrev, [sectionKey, ...path, field.key]);
        return newPrev;
      });
      setTouched((prev) => {
        const newPrev = cloneDeep(prev);
        unset(newPrev, [sectionKey, ...path, field.key]);
        return newPrev;
      });
    }

    const fieldKeyPrefix = `${fieldKey}:${newFieldQueryKey}:`;
    data.forEach((item) => {
      const normalizedFields = parseFieldResponse(item, fieldKeyPrefix);
      if (!normalizedFields?.length) {
        return;
      }

      flattenArray(normalizedFields).forEach((normalizedField) => {
        dispatchStoreAction(createSetFieldAction(normalizedField.key, normalizedField));
      });
    });

    dispatchStoreAction(
      createUpdateFieldCacheAction(newFieldQueryKey, {
        data: data.map((d) => `${fieldKeyPrefix}${d.key}`),
        isLoading: false,
        error: undefined,
      })
    );
  });

export const useFieldQuery = (sectionKey: string, fieldKey: string, path: string[] = []) => {
  const {
    store,
    dispatchStoreAction,
    setValues,
    setTouched,
    values,
    errors,
    setOptionalFieldsOverrides,
  } = useContext(ContestManageContext);
  const field = useMemo(() => store.fields[fieldKey], [fieldKey, store]);

  const fieldQuery = useMemo(() => {
    if (!field) {
      return;
    }

    return getFieldQueryByField(store, field);
  }, [field, store]);

  const isVisible = useMemo(() => {
    if (!fieldQuery) {
      return false;
    }

    if (fieldQuery.type === 'DynamicFields' || fieldQuery.type === 'DynamicField') {
      if ((field.type !== 'DynamicFields' && field.type !== 'DynamicField') || !field.dependsOn) {
        return true;
      }

      const dependencyValues = getDependenciesValues(values, sectionKey, field.dependsOn);
      return getIsDependenciesSatisfied(sectionKey, field.dependsOn, errors, dependencyValues);
    }

    if (!fieldQuery.data.dependsOn) {
      return true;
    }

    const dependencyValues = getDependenciesValues(values, sectionKey, fieldQuery.data.dependsOn);
    return getIsDependenciesSatisfied(
      sectionKey,
      fieldQuery.data.dependsOn,
      errors,
      dependencyValues
    );
  }, [errors, fieldQuery, sectionKey, values, field]);

  useEffect(() => {
    if (!fieldQuery) {
      return;
    }

    if (fieldQuery.type === 'DynamicFields' || fieldQuery.type === 'DynamicField') {
      return;
    }

    if (!fieldQuery.data.dependsOn) {
      return;
    }

    if (!('validation' in fieldQuery.data) || !fieldQuery.data.validation?.isOptionalWhenHidden) {
      return;
    }

    setOptionalFieldsOverrides((prev) => {
      let currentSection = prev[sectionKey] || [];
      const keyWithPath = [...path, fieldQuery.data.key].join('.');
      if (isVisible) {
        if (currentSection.includes(keyWithPath)) {
          currentSection = currentSection.filter((key) => key !== keyWithPath);
        }
      } else if (!currentSection.includes(keyWithPath)) {
        currentSection = [...currentSection, keyWithPath];
      }

      return {
        ...prev,
        [sectionKey]: currentSection,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fieldKey,
    fieldQuery,
    isVisible,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(path),
    sectionKey,
    setOptionalFieldsOverrides,
  ]);

  const fetchFieldData = useCallback(
    (
      newFieldQueryKey: string,
      payload: FormValues,
      abortController: AbortController,
      currentFieldQuery: FieldQueryData | null
    ) => {
      if (field.type === 'DynamicField') {
        return ensureDataForDynamicField({
          field,
          path,
          fieldKey,
          sectionKey,
          setValues,
          setTouched,
          dispatchStoreAction,
          payload,
          newFieldQueryKey,
          abortController,
          currentFieldQuery,
        });
      }
      if (field.type === 'DynamicFields') {
        return ensureDataForDynamicFields({
          field,
          path,
          fieldKey,
          sectionKey,
          setValues,
          setTouched,
          dispatchStoreAction,
          payload,
          newFieldQueryKey,
          abortController,
          currentFieldQuery,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatchStoreAction,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(field),
      fieldKey,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(path),
      sectionKey,
      setTouched,
      setValues,
    ]
  );

  useEffect(() => {
    if (isVisible) {
      return;
    }

    if (fieldQuery.type === 'DynamicFields') {
      if (!fieldQuery.data) return;
      fieldQuery.data.forEach((childQueryKey) => {
        const childField = store.fields[childQueryKey];
        const childFieldQuery = getFieldQueryByField(store, childField);

        if (!childFieldQuery || !childFieldQuery.data || childFieldQuery.type === 'DynamicFields') {
          return;
        }

        setValues((prev) => {
          const newPrev = cloneDeep(prev);
          unset(newPrev, [sectionKey, ...path, childFieldQuery.data?.key]);
          return newPrev;
        });
        setTouched((prev) => {
          const newPrev = cloneDeep(prev);
          unset(newPrev, [sectionKey, ...path, childFieldQuery.data?.key]);
          return newPrev;
        });
      });
      return;
    }
    setValues((prev) => {
      const newPrev = cloneDeep(prev);
      unset(newPrev, [sectionKey, ...path, fieldQuery.data?.key]);
      return newPrev;
    });
    setTouched((prev) => {
      const newPrev = cloneDeep(prev);
      unset(newPrev, [sectionKey, ...path, fieldQuery.data?.key]);
      return newPrev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, setValues, setTouched, sectionKey, JSON.stringify(path), field?.key]);

  const { retryFn } = useEnsureFieldData({
    field,
    fieldKey,
    sectionKey,
    queryFn: fetchFieldData,
    setValues,
    setTouched,
    path,
  });

  return {
    field,
    fieldQuery,
    isVisible,
    retryFn,
  };
};
